import { makeStyles, tokens } from '@fluentui/react-components';
import { APPROVED_COLOR, DENIED_COLOR, PENDING_COLOR } from '../constants/table';

export const useFiscalEndReviewPageStyles = makeStyles({
  statusIconApproved: {
    color: APPROVED_COLOR,
    fontSize: '16px',
  },
  statusIconDenied: {
    color: DENIED_COLOR,
    fontSize: '16px',
  },
  statusIconPending: {
    color: PENDING_COLOR,
    fontSize: '16px',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  actionGroup: {
    display: 'flex',
    gap: '4px',
  },
  actionButton: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '4px',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },

  pageRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  subtitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap',
  },
  lastUpdated: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: tokens.colorNeutralForeground3,
  },

  ttmInfoRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginBottom: '20px',
  },

  // Toolbar
  toolbar: {
    marginBottom: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: '8px',
  },

  // Filters
  filtersRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },

  // Table
  tableWrapper: {
    overflowX: 'auto' as const,
    overflowY: 'auto' as const,
    width: '100%',
    flex: 1,
    minHeight: '200px',
    scrollbarWidth: 'thin' as const,
  },
  tableHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    // Cover all resizable columns, including those outside the horizontal viewport.
    width: 'max-content',
    minWidth: '100%',
  },

  // Partner link
  partnerLink: {
    color: tokens.colorBrandForegroundLink,
    fontWeight: 'normal',
    cursor: 'pointer',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },

  // Notes icon
  dash: {
    color: tokens.colorNeutralForeground3,
  },

  // Shared layout
  messageBarWrapper: {
    marginBottom: '16px',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '48px 24px',
    color: tokens.colorNeutralForeground3,
  },
  mutedText: {
    color: tokens.colorNeutralForeground3,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },

  // Notes editor
  notesSaveRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },

  // Drawer
  drawerSectionTitle: {
    fontWeight: 600,
    marginBottom: '12px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  detailColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  drawerTabContent: {
    marginTop: '16px',
    paddingBottom: '24px',
  },
  drawerNavActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  detailValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  drawerDivider: {
    border: 'none',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    margin: '20px 0',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
  },
  timelineEntry: {
    display: 'flex',
    gap: '12px',
  },
  timelineTrack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    width: '24px',
  },
  timelineIcon: {
    fontSize: '20px',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  timelineLine: {
    width: '2px',
    flexGrow: 1,
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: '4px',
    marginBottom: '4px',
    minHeight: '24px',
  },
  timelineContent: {
    paddingBottom: '20px',
  },
  timelineDate: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  timelineAction: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    marginTop: '2px',
  },
  timelineDetail: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },

  // Notes history table
  notesTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  notesHeaderRow: {
    display: 'flex',
    gap: '12px',
    paddingBottom: '8px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    marginBottom: '4px',
  },
  notesHeaderCell: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  notesRow: {
    display: 'flex',
    gap: '12px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  notesCell: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
});
